import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Shield, ChevronDown, ChevronUp, CheckCircle, Star, Eye } from 'lucide-react';
import type { MortgageData } from '../../CalculatorTabs';
import type { CalculatedLoanProgram } from '../utils/mortgageUtils';
import { calculateMortgagePayment } from '../utils/mortgageUtils';
import { calculateFundingFee } from '../../utils/fundingFeeCalculations';

interface VALoanSelectorProps {
  mortgageData: MortgageData;
  selectedTerm: string;
  onViewDetails: (program: CalculatedLoanProgram) => void;
  onSelectProgram: (program: CalculatedLoanProgram) => void;
  selectedProgram: string | null;
}

interface VAVariant {
  id: string;
  name: string;
  vaStatus: 'activeDuty' | 'separatedRetired';
  vaDisabilityStatus: 'none' | 'partial' | 'full';
  homebuyingHistory: 'firstTime' | 'other';
  label: string;
  description: string;
}

export const VALoanSelector: React.FC<VALoanSelectorProps> = ({
  mortgageData,
  selectedTerm,
  onViewDetails,
  onSelectProgram,
  selectedProgram
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Define all VA variants to display
  const vaVariants: VAVariant[] = useMemo(() => {
    const isFirstTime = mortgageData.homebuyingHistory === 'firstTime';
    
    return [
      {
        id: 'va-active-duty-no-rating',
        name: 'Active Duty - No Rating',
        vaStatus: 'activeDuty',
        vaDisabilityStatus: 'none',
        homebuyingHistory: isFirstTime ? 'firstTime' : 'other',
        label: 'Active Duty - No Disability',
        description: '2.15% funding fee'
      },
      {
        id: 'va-active-duty-partial',
        name: 'Active Duty - 10%-90%',
        vaStatus: 'activeDuty',
        vaDisabilityStatus: 'partial',
        homebuyingHistory: isFirstTime ? 'firstTime' : 'other',
        label: 'Active Duty - 10%-90% Rating',
        description: 'No funding fee'
      },
      {
        id: 'va-active-duty-full',
        name: 'Active Duty - 100%',
        vaStatus: 'activeDuty',
        vaDisabilityStatus: 'full',
        homebuyingHistory: isFirstTime ? 'firstTime' : 'other',
        label: 'Active Duty - 100% Rating',
        description: 'No funding fee, best rates'
      },
      {
        id: 'va-separated-no-rating',
        name: 'Separated/Retired - No Rating',
        vaStatus: 'separatedRetired',
        vaDisabilityStatus: 'none',
        homebuyingHistory: isFirstTime ? 'firstTime' : 'other',
        label: 'Separated/Retired - No Disability',
        description: isFirstTime ? '2.15% funding fee' : '3.3% funding fee'
      },
      {
        id: 'va-separated-partial',
        name: 'Separated/Retired - 10%-90%',
        vaStatus: 'separatedRetired',
        vaDisabilityStatus: 'partial',
        homebuyingHistory: isFirstTime ? 'firstTime' : 'other',
        label: 'Separated/Retired - 10%-90% Rating',
        description: 'No funding fee'
      },
      {
        id: 'va-separated-full',
        name: 'Separated/Retired - 100%',
        vaStatus: 'separatedRetired',
        vaDisabilityStatus: 'full',
        homebuyingHistory: isFirstTime ? 'firstTime' : 'other',
        label: 'Separated/Retired - 100% Rating',
        description: 'No funding fee, best rates'
      }
    ];
  }, [mortgageData.homebuyingHistory]);

  // Calculate loan details for each variant
  const calculatedVariants = useMemo(() => {
    return vaVariants.map(variant => {
      const baseRate = mortgageData.interestRate || 6.5;
      const creditScoreAdjustment = mortgageData.creditScore >= 740 ? -0.5 : 
                                    mortgageData.creditScore >= 680 ? -0.25 : 0;
      
      // Best rates for 100% disabled
      let finalRate = variant.vaDisabilityStatus === 'full' 
        ? Math.max(baseRate - 0.75 + creditScoreAdjustment, 3.0)
        : Math.max(baseRate - 0.5 + creditScoreAdjustment, 3.0);

      const loanAmount = mortgageData.homePrice; // VA allows 0% down
      const monthlyPayment = calculateMortgagePayment(loanAmount, finalRate, parseInt(selectedTerm));
      
      // Calculate funding fee
      const fundingFeeInfo = calculateFundingFee(
        'va',
        loanAmount,
        mortgageData.homePrice,
        0, // VA no down payment
        0, // VA no down payment
        variant.homebuyingHistory === 'firstTime',
        variant.vaStatus,
        variant.homebuyingHistory,
        variant.vaDisabilityStatus
      );

      const totalCost = (monthlyPayment * parseInt(selectedTerm) * 12) + fundingFeeInfo.amount;

      const program: CalculatedLoanProgram = {
        id: variant.id,
        name: variant.name,
        baseRate: finalRate,
        downPaymentPercent: 0,
        eligibilityScore: variant.vaDisabilityStatus === 'full' ? 100 : 
                         variant.vaDisabilityStatus === 'partial' ? 95 : 90,
        recommended: variant.vaStatus === mortgageData.vaStatus && 
                    variant.vaDisabilityStatus === mortgageData.vaDisabilityStatus,
        eligibleForUser: true,
        description: variant.description,
        benefits: [
          'No down payment',
          'No PMI',
          variant.vaDisabilityStatus !== 'none' ? 'No funding fee' : `${fundingFeeInfo.percentage}% funding fee`,
          'Competitive rates',
          'Assumable loan',
          ...(mortgageData.state === 'TX' && variant.vaDisabilityStatus === 'full' ? ['TX Property tax 100% EXEMPT'] : 
              mortgageData.state === 'TX' && variant.vaDisabilityStatus !== 'none' ? ['TX Property tax exemptions'] : [])
        ],
        monthlyPayment: Math.round(monthlyPayment),
        totalCost: Math.round(totalCost),
        downPayment: 0,
        downPercent: 0,
        apr: finalRate,
        vaStatus: variant.vaStatus,
        vaDisabilityStatus: variant.vaDisabilityStatus,
        fundingFeePercent: fundingFeeInfo.percentage
      };

      return {
        variant,
        program,
        fundingFeeAmount: fundingFeeInfo.amount
      };
    });
  }, [vaVariants, mortgageData, selectedTerm]);

  // Find best option based on total cost
  const bestOption = useMemo(() => {
    return calculatedVariants.reduce((best, current) => 
      current.program.totalCost < best.program.totalCost ? current : best
    );
  }, [calculatedVariants]);

  // Get current user's matching variant
  const currentUserVariant = useMemo(() => {
    return calculatedVariants.find(cv => 
      cv.variant.vaStatus === mortgageData.vaStatus &&
      cv.variant.vaDisabilityStatus === mortgageData.vaDisabilityStatus
    );
  }, [calculatedVariants, mortgageData.vaStatus, mortgageData.vaDisabilityStatus]);

  const getStatusColor = (vaDisabilityStatus: string) => {
    switch (vaDisabilityStatus) {
      case 'full': return 'border-yellow-400/50 bg-yellow-900/10';
      case 'partial': return 'border-green-400/50 bg-green-900/10';
      default: return 'border-blue-400/50 bg-blue-900/10';
    }
  };

  const getStatusBadgeColor = (vaDisabilityStatus: string) => {
    switch (vaDisabilityStatus) {
      case 'full': return 'bg-yellow-500/20 border-yellow-500/50 text-yellow-300';
      case 'partial': return 'bg-green-500/20 border-green-500/50 text-green-300';
      default: return 'bg-blue-500/20 border-blue-500/50 text-blue-300';
    }
  };

  return (
    <div className="space-y-4">
      {/* Master VA Card */}
      <Card className="border-blue-500/50 bg-blue-900/20">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8 text-blue-400" />
              <div>
                <CardTitle className="text-2xl font-bold text-blue-100">
                  VA Loans - Compare All Options
                </CardTitle>
                <p className="text-sm text-blue-300 mt-1">
                  View and compare different VA loan scenarios based on status and disability rating
                </p>
              </div>
            </div>
            <Button 
              onClick={() => setIsExpanded(!isExpanded)}
              variant="outline"
              className="border-blue-400/50 text-blue-300 hover:bg-blue-800/30"
            >
              {isExpanded ? (
                <>
                  <ChevronUp className="w-4 h-4 mr-2" />
                  Collapse
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4 mr-2" />
                  Expand Options
                </>
              )}
            </Button>
          </div>
        </CardHeader>

        {!isExpanded && (
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-slate-800/50 rounded-lg">
                <span className="text-xs text-slate-400 uppercase tracking-wider">Best Option</span>
                <div className="text-lg font-semibold text-slate-100 mt-1">
                  {bestOption.program.name}
                </div>
                <div className="text-sm text-slate-300 mt-1">
                  {formatCurrency(bestOption.program.monthlyPayment)}/mo
                </div>
              </div>
              <div className="p-4 bg-slate-800/50 rounded-lg">
                <span className="text-xs text-slate-400 uppercase tracking-wider">Your Current Status</span>
                <div className="text-lg font-semibold text-slate-100 mt-1">
                  {currentUserVariant?.program.name || 'Not Set'}
                </div>
                {currentUserVariant && (
                  <div className="text-sm text-slate-300 mt-1">
                    {formatCurrency(currentUserVariant.program.monthlyPayment)}/mo
                  </div>
                )}
              </div>
              <div className="p-4 bg-slate-800/50 rounded-lg">
                <span className="text-xs text-slate-400 uppercase tracking-wider">Available Options</span>
                <div className="text-lg font-semibold text-slate-100 mt-1">
                  {calculatedVariants.length} Variants
                </div>
                <div className="text-sm text-slate-300 mt-1">
                  Click to compare all
                </div>
              </div>
            </div>
          </CardContent>
        )}

        {isExpanded && (
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {calculatedVariants.map(({ variant, program, fundingFeeAmount }) => {
                const isCurrentStatus = program.recommended;
                const isBestOption = program.id === bestOption.program.id;
                const isSelected = selectedProgram === program.id;

                return (
                  <Card
                    key={program.id}
                    className={`transition-all hover:shadow-lg ${getStatusColor(variant.vaDisabilityStatus)} ${
                      isSelected ? 'ring-2 ring-green-400/50' : ''
                    }`}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Shield className="w-5 h-5 text-blue-400" />
                            <CardTitle className="text-lg font-bold text-slate-100">
                              {program.name}
                            </CardTitle>
                          </div>
                          <p className="text-xs text-slate-400">
                            {variant.description}
                          </p>
                        </div>
                        <div className="flex flex-col gap-1 items-end">
                          {isBestOption && (
                            <Badge className="bg-purple-500/20 border-purple-500/50 text-purple-300 text-xs">
                              <Star className="w-3 h-3 mr-1" />
                              Lowest Cost
                            </Badge>
                          )}
                          {isCurrentStatus && (
                            <Badge className={`${getStatusBadgeColor(variant.vaDisabilityStatus)} text-xs`}>
                              Your Status
                            </Badge>
                          )}
                        </div>
                      </div>
                    </CardHeader>

                    <CardContent className="space-y-3">
                      {/* Key Metrics */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <span className="text-xs text-slate-400 uppercase tracking-wider">Monthly Payment</span>
                          <div className="text-xl font-bold text-slate-100">
                            {formatCurrency(program.monthlyPayment)}
                          </div>
                        </div>
                        <div>
                          <span className="text-xs text-slate-400 uppercase tracking-wider">Interest Rate</span>
                          <div className="text-xl font-bold text-slate-100">
                            {program.baseRate.toFixed(2)}%
                          </div>
                        </div>
                        <div>
                          <span className="text-xs text-slate-400 uppercase tracking-wider">Funding Fee</span>
                          <div className="text-lg font-semibold text-slate-200">
                            {fundingFeeAmount === 0 ? (
                              <Badge className="bg-green-500/20 border-green-500/50 text-green-300">
                                EXEMPT
                              </Badge>
                            ) : (
                              `${formatCurrency(fundingFeeAmount)}`
                            )}
                          </div>
                        </div>
                        <div>
                          <span className="text-xs text-slate-400 uppercase tracking-wider">Total Cost</span>
                          <div className="text-lg font-semibold text-slate-200">
                            {formatCurrency(program.totalCost)}
                          </div>
                        </div>
                      </div>

                      {/* Benefits */}
                      {variant.vaDisabilityStatus !== 'none' && (
                        <div className="p-2 bg-green-900/20 border border-green-500/30 rounded text-xs">
                          <div className="grid grid-cols-2 gap-1 text-green-200">
                            <div>✓ No Funding Fee</div>
                            {variant.vaDisabilityStatus === 'full' && (
                              <>
                                <div>✓ Best Rates</div>
                                {mortgageData.state === 'TX' && (
                                  <div className="col-span-2">✓ TX Property Tax 100% EXEMPT</div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex gap-2 pt-2 border-t border-slate-700">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onViewDetails(program)}
                          className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700/50"
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          Details
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => onSelectProgram(program)}
                          className={`flex-1 ${
                            isSelected 
                              ? 'bg-green-600 hover:bg-green-700 text-white' 
                              : 'bg-purple-600 hover:bg-purple-700 text-white'
                          }`}
                        >
                          {isSelected ? (
                            <>
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Selected
                            </>
                          ) : (
                            'Select'
                          )}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
};
